// views/CountriesView.jsx
// Admin country management — add, enable/disable, edit limits and gateway.
import { useState, useEffect, useCallback } from "react";
import { toast } from "react-toastify";
import { apiClient } from "../../../api/apiServices";

const GATEWAYS = ["paystack", "ivorypay", "flutterwave"];

const GATEWAY_COLORS = {
  paystack:    { text: "text-[#00C896]",  bg: "bg-[#00C896]/10"  },
  ivorypay:    { text: "text-blue-400",   bg: "bg-blue-500/10"   },
  flutterwave: { text: "text-orange-400", bg: "bg-orange-500/10" },
};

function GatewayBadge({ gateway }) {
  const c = GATEWAY_COLORS[gateway] || { text: "text-white/40", bg: "bg-white/5" };
  return (
    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${c.text} ${c.bg}`}>
      {gateway}
    </span>
  );
}

function CountryModal({ country, onClose, onSaved }) {
  const isEdit = !!country?.countryCode;
  const [form, setForm] = useState({
    countryCode:     country?.countryCode     || "",
    countryName:     country?.countryName     || "",
    currency:        country?.currency        || "",
    currencyName:    country?.currencyName    || "",
    symbol:          country?.symbol          || "",
    flag:            country?.flag            || "",
    gateway:         country?.gateway         || "ivorypay",
    minReceivable:   country?.minReceivable   ?? 1,
    maxReceivable:   country?.maxReceivable   ?? 0,
    fxMarginPercent: country?.fxMarginPercent ?? 0,
    enabled:         country?.enabled         ?? true,
    order:           country?.order           ?? 99,
  });
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const handleSave = async () => {
    if (!form.countryCode || !form.countryName || !form.currency) {
      toast.error("Country code, name and currency are required");
      return;
    }
    setSaving(true);
    try {
      if (isEdit) {
        await apiClient.patch(`/mtrade/admin/countries/${form.countryCode}`, form);
        toast.success(`${form.countryName} updated`);
      } else {
        await apiClient.post("/mtrade/admin/countries", form);
        toast.success(`${form.countryName} added`);
      }
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to save");
    } finally { setSaving(false); }
  };

  const fields = [
    { key: "countryCode",  label: "Country Code (ISO 2)",  placeholder: "NG",      disabled: isEdit, upper: true },
    { key: "countryName",  label: "Country Name",          placeholder: "Nigeria"              },
    { key: "currency",     label: "Currency Code",         placeholder: "NGN",     upper: true },
    { key: "currencyName", label: "Currency Name",         placeholder: "Nigerian Naira"       },
    { key: "symbol",       label: "Currency Symbol",       placeholder: "₦"                    },
    { key: "flag",         label: "Flag Emoji",            placeholder: "🇳🇬"                  },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70">
      <div className="bg-[#1a1a1a] border border-white/10 rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto shadow-2xl">
        <div className="flex items-center justify-between p-6 border-b border-white/8">
          <h3 className="text-white font-bold text-lg">{isEdit ? "Edit Country" : "Add Country"}</h3>
          <button onClick={onClose} className="text-white/30 hover:text-white/60">
            <i className="ri-close-line text-xl" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            {fields.map(f => (
              <div key={f.key} className={f.key === "countryName" || f.key === "currencyName" ? "col-span-2" : ""}>
                <label className="text-white/40 text-[10px] uppercase tracking-wider font-semibold block mb-1.5">
                  {f.label}
                </label>
                <input
                  value={form[f.key]}
                  onChange={e => set(f.key, f.upper ? e.target.value.toUpperCase() : e.target.value)}
                  placeholder={f.placeholder}
                  disabled={f.disabled}
                  className="w-full bg-[#111] border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white
                    placeholder-white/20 focus:outline-none focus:border-[#00C896]/50 disabled:opacity-40"
                />
              </div>
            ))}
          </div>

          {/* Gateway */}
          <div>
            <label className="text-white/40 text-[10px] uppercase tracking-wider font-semibold block mb-1.5">
              Payment Gateway
            </label>
            <div className="flex gap-2">
              {GATEWAYS.map(g => (
                <button key={g} onClick={() => set("gateway", g)}
                  className={`flex-1 py-2 rounded-xl text-xs font-bold border transition-all ${
                    form.gateway === g
                      ? `${GATEWAY_COLORS[g].text} ${GATEWAY_COLORS[g].bg} border-current/30`
                      : "text-white/30 bg-[#111] border-white/8 hover:border-white/20"
                  }`}>
                  {g}
                </button>
              ))}
            </div>
          </div>

          {/* Limits */}
          <div className="grid grid-cols-3 gap-3">
            {[
              { key: "minReceivable",   label: "Min Receivable",  type: "number" },
              { key: "maxReceivable",   label: "Max (0 = none)",  type: "number" },
              { key: "fxMarginPercent", label: "FX Margin %",     type: "number" },
            ].map(f => (
              <div key={f.key}>
                <label className="text-white/40 text-[10px] uppercase tracking-wider font-semibold block mb-1.5">
                  {f.label}
                </label>
                <input type="number" value={form[f.key]}
                  onChange={e => set(f.key, Number(e.target.value))}
                  className="w-full bg-[#111] border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-[#00C896]/50"
                />
              </div>
            ))}
          </div>

          {/* Enabled + Order */}
          <div className="flex items-center justify-between bg-[#111] border border-white/8 rounded-xl px-4 py-3">
            <div>
              <p className="text-white/70 text-sm font-semibold">Enabled</p>
              <p className="text-white/30 text-xs">Users from this country can register and transact</p>
            </div>
            <button onClick={() => set("enabled", !form.enabled)}
              className={`relative w-11 h-6 rounded-full transition-colors ${form.enabled ? "bg-[#00C896]" : "bg-white/15"}`}>
              <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-all ${form.enabled ? "left-5" : "left-0.5"}`} />
            </button>
          </div>
        </div>

        <div className="flex gap-3 p-6 border-t border-white/8">
          <button onClick={onClose}
            className="flex-1 py-3 rounded-xl bg-white/5 text-white/40 text-sm font-semibold hover:bg-white/10">
            Cancel
          </button>
          <button onClick={handleSave} disabled={saving}
            className="flex-1 py-3 rounded-xl bg-[#00C896] text-black text-sm font-bold disabled:opacity-50 flex items-center justify-center gap-2">
            {saving && <div className="w-3.5 h-3.5 border-2 border-black/20 border-t-black rounded-full animate-spin" />}
            {saving ? "Saving…" : isEdit ? "Save Changes" : "Add Country"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function CountriesView() {
  const [countries, setCountries] = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [modal,     setModal]     = useState(null);   // null | {} | {country}
  const [toggling,  setToggling]  = useState(null);
  const [deleting,  setDeleting]  = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await apiClient.get("/mtrade/admin/countries");
      if (res.data.status) setCountries(res.data.data);
    } catch { toast.error("Failed to load countries"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleToggle = async (countryCode, name) => {
    setToggling(countryCode);
    try {
      const res = await apiClient.patch(`/mtrade/admin/countries/${countryCode}/toggle`);
      if (res.data.status) {
        toast.success(res.data.message);
        setCountries(prev => prev.map(c =>
          c.countryCode === countryCode ? { ...c, enabled: !c.enabled } : c
        ));
      } else toast.error(res.data.message);
    } catch (err) { toast.error(err?.response?.data?.message || "Failed"); }
    finally { setToggling(null); }
  };

  const handleDelete = async (country) => {
    if (!window.confirm(`Remove ${country.countryName}? This cannot be undone.`)) return;
    setDeleting(country.countryCode);
    try {
      await apiClient.delete(`/mtrade/admin/countries/${country.countryCode}`);
      toast.success(`${country.countryName} removed`);
      setCountries(prev => prev.filter(c => c.countryCode !== country.countryCode));
    } catch (err) { toast.error(err?.response?.data?.message || "Failed to delete"); }
    finally { setDeleting(null); }
  };

  const enabledCount  = countries.filter(c => c.enabled).length;
  const disabledCount = countries.filter(c => !c.enabled).length;

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h2 className="text-lg font-bold text-white">Countries & Markets</h2>
          <p className="text-white/40 text-xs mt-0.5">
            {enabledCount} active · {disabledCount} disabled · Controls which markets Easwallet serves
          </p>
        </div>
        <button onClick={() => setModal({})}
          className="flex items-center gap-2 px-4 py-2.5 bg-[#00C896] text-black rounded-xl text-sm font-bold hover:bg-[#00b386]">
          <i className="ri-add-line" /> Add Country
        </button>
      </div>

      {/* Summary row */}
      <div className="grid grid-cols-3 gap-3">
        {[
          { label: "Active Markets",    value: enabledCount,  icon: "ri-earth-line",      color: "#00C896" },
          { label: "Pending Launch",    value: disabledCount, icon: "ri-time-line",       color: "#fbbf24" },
          { label: "Gateways in use",   value: [...new Set(countries.filter(c=>c.enabled).map(c=>c.gateway))].length,
            icon: "ri-bank-card-line", color: "#a78bfa" },
        ].map(s => (
          <div key={s.label} className="bg-[#161616] border border-white/5 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: s.color + "20" }}>
                <i className={`${s.icon} text-sm`} style={{ color: s.color }} />
              </div>
              <p className="text-white/40 text-xs">{s.label}</p>
            </div>
            <p className="text-white font-bold text-xl">{s.value}</p>
          </div>
        ))}
      </div>

      {/* Countries table */}
      <div className="bg-[#161616] border border-white/5 rounded-xl overflow-hidden">
        <div className="hidden md:grid px-4 py-2.5 border-b border-white/5 text-[9px] uppercase tracking-widest font-bold text-white/20"
          style={{ gridTemplateColumns: "2.5rem 1fr 6rem 7rem 6rem 6rem 5rem 4rem" }}>
          <span />
          <span>Country</span>
          <span>Code</span>
          <span>Currency</span>
          <span>Gateway</span>
          <span>Min/Max</span>
          <span className="text-center">Status</span>
          <span className="text-center">Actions</span>
        </div>

        {loading ? (
          <div className="divide-y divide-white/4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="flex items-center gap-4 px-4 py-4">
                <div className="w-8 h-8 bg-white/5 rounded-lg animate-pulse flex-shrink-0" />
                <div className="flex-1 h-4 bg-white/5 rounded animate-pulse" />
                <div className="w-20 h-4 bg-white/5 rounded animate-pulse" />
                <div className="w-24 h-4 bg-white/5 rounded animate-pulse" />
              </div>
            ))}
          </div>
        ) : countries.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16">
            <i className="ri-earth-line text-white/10 text-4xl mb-2" />
            <p className="text-white/25 text-sm">No countries configured</p>
          </div>
        ) : (
          <div className="divide-y divide-white/4">
            {countries.map(c => (
              <div key={c.countryCode}
                className={`grid items-center gap-3 px-4 py-4 ${!c.enabled ? "opacity-50" : ""}`}
                style={{ gridTemplateColumns: "2.5rem 1fr 6rem 7rem 6rem 6rem 5rem 4rem" }}>

                {/* Flag */}
                <span className="text-xl">{c.flag || "🌍"}</span>

                {/* Name */}
                <div>
                  <p className="text-white font-semibold text-sm">{c.countryName}</p>
                  {c.isBase && (
                    <span className="text-[9px] font-bold text-[#00C896] bg-[#00C896]/10 px-1.5 py-0.5 rounded-full">
                      Base
                    </span>
                  )}
                </div>

                {/* Code */}
                <p className="text-white/50 text-sm font-mono">{c.countryCode}</p>

                {/* Currency */}
                <div>
                  <p className="text-white/70 text-sm">{c.currency} <span className="text-white/30">{c.symbol}</span></p>
                  <p className="text-white/25 text-xs">{c.currencyName}</p>
                </div>

                {/* Gateway */}
                <GatewayBadge gateway={c.gateway} />

                {/* Min/Max */}
                <div className="text-white/30 text-xs">
                  <p>Min: {c.minReceivable?.toLocaleString()}</p>
                  <p>Max: {c.maxReceivable > 0 ? c.maxReceivable.toLocaleString() : "—"}</p>
                </div>

                {/* Status toggle */}
                <div className="flex justify-center">
                  <button
                    onClick={() => !c.isBase && handleToggle(c.countryCode, c.countryName)}
                    disabled={!!toggling || c.isBase}
                    className={`relative w-10 h-5 rounded-full transition-colors disabled:cursor-not-allowed ${
                      c.enabled ? "bg-[#00C896]" : "bg-white/15"
                    } ${c.isBase ? "opacity-40" : ""}`}>
                    <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-all ${
                      c.enabled ? "left-5" : "left-0.5"
                    }`} />
                  </button>
                </div>

                {/* Actions */}
                <div className="flex items-center justify-center gap-1.5">
                  <button onClick={() => setModal(c)}
                    className="w-7 h-7 rounded-lg bg-white/5 hover:bg-white/10 flex items-center justify-center text-white/30 hover:text-white/70">
                    <i className="ri-pencil-line text-sm" />
                  </button>
                  {!c.isBase && (
                    <button onClick={() => handleDelete(c)} disabled={deleting === c.countryCode}
                      className="w-7 h-7 rounded-lg bg-red-500/8 hover:bg-red-500/15 flex items-center justify-center text-red-400/50 hover:text-red-400">
                      <i className="ri-delete-bin-line text-sm" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Info box */}
      <div className="flex items-start gap-3 px-4 py-3.5 bg-blue-500/5 border border-blue-500/10 rounded-xl">
        <i className="ri-information-line text-blue-400 mt-0.5 flex-shrink-0" />
        <div>
          <p className="text-blue-300 text-xs font-semibold mb-0.5">How enabling a country works</p>
          <p className="text-blue-300/60 text-xs leading-relaxed">
            Toggling a country ON immediately allows users from that country to register and transact.
            Make sure the gateway assigned to it is also enabled in Config Settings
            (payments.enabled_gateways). Changes take effect within 60 seconds.
          </p>
        </div>
      </div>

      {/* Modal */}
      {modal !== null && (
        <CountryModal
          country={Object.keys(modal).length > 0 ? modal : null}
          onClose={() => setModal(null)}
          onSaved={load}
        />
      )}
    </div>
  );
}
